/**
 * Browser Mode Store
 *
 * Zustand store for managing the browser mode state, including database
 * connections and UI state.
 */

import { create } from 'zustand';
import type { BrowserModeDatabase, BrowserModeConnectionState, BrowserModeConnectionStatus, UploadedDatabase } from '../types/browserModeTypes';

type LoadingProgress = {
  phase: 'fetching' | 'parsing' | 'caching' | 'ready';
  progress: number;
};

/** File size warning/error state */
type FileSizeWarning = {
  type: 'warning' | 'error';
  message: string;
};

type BrowserModeState = {
  /** Currently active browser mode connection, or null if none */
  activeConnection: BrowserModeConnectionState | null;
  /** ID of the database currently being loaded (for loading state) */
  loadingDatabaseId: string | null;
  /** Loading progress for showing progress UI */
  loadingProgress: LoadingProgress | null;
  /** Whether persistence is available in the browser */
  persistenceAvailable: boolean;
  /** Whether the current database has unsaved modifications */
  hasModifications: boolean;
  /** Whether a reset is in progress */
  isResetting: boolean;
  /** List of uploaded databases for switching */
  uploadedDatabases: UploadedDatabase[];
  /** File size warning/error for upload dialog */
  fileSizeWarning: FileSizeWarning | null;
  /** Error message from file validation */
  uploadError: string | null;
  /** When true, the connections dialog should auto-open the file picker */
  autoOpenFilePicker: boolean;
};

type BrowserModeActions = {
  /** Start connecting to a database */
  startConnecting: (database: BrowserModeDatabase | UploadedDatabase) => void;
  /** Mark connection as successful */
  setConnected: (database: BrowserModeDatabase | UploadedDatabase) => void;
  /** Mark connection as failed with error */
  setConnectionError: (database: BrowserModeDatabase | UploadedDatabase, error: string) => void;
  /** Disconnect from current database */
  disconnect: () => void;
  /** Update connection status */
  setConnectionStatus: (status: BrowserModeConnectionStatus) => void;
  /** Update loading progress */
  setLoadingProgress: (progress: LoadingProgress | null) => void;
  /** Set persistence availability */
  setPersistenceAvailable: (available: boolean) => void;
  /** Set modifications flag */
  setHasModifications: (has: boolean) => void;
  /** Set resetting state */
  setIsResetting: (resetting: boolean) => void;
  /** Add an uploaded database to the list */
  addUploadedDatabase: (db: UploadedDatabase) => void;
  /** Remove an uploaded database from the list */
  removeUploadedDatabase: (id: string) => void;
  /** Set file size warning */
  setFileSizeWarning: (warning: FileSizeWarning | null) => void;
  /** Set upload error */
  setUploadError: (error: string | null) => void;
  /** Clear upload state (warnings, errors) */
  clearUploadState: () => void;
  /** Request that the connections dialog auto-opens the file picker */
  requestAutoOpenFilePicker: () => void;
  /** Consume the auto-open file picker request */
  consumeAutoOpenFilePicker: () => void;
};

export const useBrowserModeStore = create<BrowserModeState & BrowserModeActions>((set) => ({
  // Initial state
  activeConnection: null,
  loadingDatabaseId: null,
  loadingProgress: null,
  persistenceAvailable: true,
  hasModifications: false,
  isResetting: false,
  uploadedDatabases: [],
  fileSizeWarning: null,
  uploadError: null,
  autoOpenFilePicker: false,

  // Actions
  startConnecting: (database: BrowserModeDatabase | UploadedDatabase) => {
    set({
      loadingDatabaseId: database.id,
      loadingProgress: { phase: 'fetching', progress: 0 },
      activeConnection: {
        database,
        status: 'connecting',
      },
    });
  },

  setConnected: (database: BrowserModeDatabase | UploadedDatabase) => {
    set({
      loadingDatabaseId: null,
      loadingProgress: null,
      hasModifications: false,
      activeConnection: {
        database,
        status: 'connected',
      },
    });
  },

  setConnectionError: (database: BrowserModeDatabase | UploadedDatabase, error: string) => {
    set({
      loadingDatabaseId: null,
      loadingProgress: null,
      activeConnection: {
        database,
        status: 'error',
        error,
      },
    });
  },

  disconnect: () => {
    set({
      activeConnection: null,
      loadingDatabaseId: null,
      loadingProgress: null,
      hasModifications: false,
    });
  },

  setConnectionStatus: (status: BrowserModeConnectionStatus) => {
    set((state) => {
      if (!state.activeConnection) return state;
      return {
        activeConnection: {
          ...state.activeConnection,
          status,
        },
      };
    });
  },

  setLoadingProgress: (progress: LoadingProgress | null) => {
    set({ loadingProgress: progress });
  },

  setPersistenceAvailable: (available: boolean) => {
    set({ persistenceAvailable: available });
  },

  setHasModifications: (has: boolean) => {
    set({ hasModifications: has });
  },

  setIsResetting: (resetting: boolean) => {
    set({ isResetting: resetting });
  },

  addUploadedDatabase: (db: UploadedDatabase) => {
    set((state) => ({
      uploadedDatabases: [...state.uploadedDatabases, db],
    }));
  },

  removeUploadedDatabase: (id: string) => {
    set((state) => ({
      uploadedDatabases: state.uploadedDatabases.filter((db) => db.id !== id),
    }));
  },

  setFileSizeWarning: (warning: FileSizeWarning | null) => {
    set({ fileSizeWarning: warning });
  },

  setUploadError: (error: string | null) => {
    set({ uploadError: error });
  },

  clearUploadState: () => {
    set({
      fileSizeWarning: null,
      uploadError: null,
    });
  },

  requestAutoOpenFilePicker: () => {
    set({ autoOpenFilePicker: true });
  },

  consumeAutoOpenFilePicker: () => {
    set({ autoOpenFilePicker: false });
  },
}));
